// MALAT — Cinematic splash before login/dashboard. ~4.5s duration, then
// routes to /dashboard (if authed) or /login. Only shown once per session.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import "./Splash.css";

const SEEN_KEY = "malat_splash_seen_v1";
const DURATION = 4500;

const Icon = ({ paths }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#D9B26A" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);
const ICONS = [
  { label: "INCOME", paths: <><path d="M4 16L10 10L14 14L20 6"/><path d="M20 11V6H15"/></> },
  { label: "EXPENSE", paths: <><path d="M4 8L10 14L14 10L20 18"/><path d="M20 13V18H15"/></> },
  { label: "SAVINGS", paths: <><rect x="4" y="5" width="16" height="15" rx="2.5"/><circle cx="12" cy="12.5" r="4"/><path d="M12 10.3V12.5L13.4 13.6" strokeWidth="1.1"/><path d="M9 5V3.3A1 1 0 0110 2.3h4a1 1 0 011 1V5" strokeWidth="1.1"/></> },
  { label: "LOANS", paths: <><circle cx="9" cy="9" r="5"/><circle cx="15" cy="15" r="5"/></> },
];

function useFonts() {
  useEffect(() => {
    if (document.getElementById("malat-fonts")) return;
    const l = document.createElement("link"); l.id = "malat-fonts"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600&family=Tajawal:wght@300;400;500;700&display=swap";
    document.head.appendChild(l);
  }, []);
}

function useThemeColor(color) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", color);
    // Also paint <html> and <body> so no light strip peeks from the safe-area / status-bar
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyBg = document.body.style.background;
    document.documentElement.style.background = color;
    document.body.style.background = color;
    document.documentElement.classList.add("malat-splash-active");
    return () => {
      if (meta && prev != null) meta.setAttribute("content", prev);
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.background = prevBodyBg;
      document.documentElement.classList.remove("malat-splash-active");
    };
  }, [color]);
}

export default function Splash() {
  useFonts();
  useThemeColor("#07080B");
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [play, setPlay] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) { nav(user ? "/dashboard" : "/login", { replace: true }); return; }
    const t1 = setTimeout(() => setPlay(true), 30);
    const t2 = setTimeout(() => {
      if (doneRef.current) return;
      doneRef.current = true;
      sessionStorage.setItem(SEEN_KEY, "1");
      nav(user ? "/dashboard" : "/login", { replace: true });
    }, DURATION);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [nav, user, loading]);

  return (
    <div className="malat-splash" data-testid="malat-splash">
      <div className={`malat-stage ${play ? "malat-play" : ""}`}>
        <div className="malat-glow" />
        {[[22,10,7,.2],[68,18,9,1.4],[40,6,8,2.6],[78,30,6.5,.8],[15,34,10,3.2]].map(([l,b,d,dl],i) => (
          <div key={i} className="malat-dust" style={{ width: 2.5, height: 2.5, left: `${l}%`, bottom: `${b}%`, animationDuration: `${d}s`, animationDelay: `${dl}s` }} />
        ))}
        <div className="malat-brand-top">M A L A T</div>
        <div className="malat-center">
          <div className="malat-curtain">
            <h1 className="malat-wordmark">MALAT<span className="malat-shimmer">MALAT</span></h1>
          </div>
          <div className="malat-rule" />
          <div className="malat-tagline">CONTROL YOUR MONEY</div>
          <div className="malat-icon-row">
            {ICONS.map((ic) => (
              <div key={ic.label} className="malat-icon-item">
                <Icon paths={ic.paths} />
                <span>{ic.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="malat-cut" />
      </div>
    </div>
  );
}
